import { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, ClipboardCopy, RotateCcw, Info } from "lucide-react";

const GBP = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" });
const gbp = (n) => (Number.isFinite(n) ? GBP.format(n) : "—");
const num = (v) => {
  const x = Number.parseFloat(v);
  return Number.isFinite(x) && x >= 0 ? x : 0;
};

const DEFAULTS = {
  weeklyIncome: 450,
  weeklyCosts: 55,
  paidHours: 25,
  unpaidHours: 4,
  weeklyMiles: 60,
  mileageRate: 0.45,
  roundTo: 0.5,
};

function Field({ id, label, hint, value, onChange, step = "0.5", min = "0", prefix, suffix }) {
  return (
    <label htmlFor={id} className="block">
      <span className="text-sm font-medium text-ink">{label}</span>
      {hint && <span className="block text-xs text-ink-muted mt-1 leading-snug">{hint}</span>}
      <div className="mt-2 flex items-stretch rounded-xl border border-slate-200 bg-white focus-within:border-purple focus-within:ring-2 focus-within:ring-purple/20 overflow-hidden">
        {prefix && <span className="px-3 flex items-center text-sm text-ink-muted bg-slate-50 border-r border-slate-200">{prefix}</span>}
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min={min}
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`hourly-input-${id}`}
          className="flex-1 px-3 py-2.5 text-sm bg-transparent focus:outline-none"
        />
        {suffix && <span className="px-3 flex items-center text-sm text-ink-muted bg-slate-50 border-l border-slate-200">{suffix}</span>}
      </div>
    </label>
  );
}

function Stat({ label, value, note, testid, big }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 ${big ? "border-purple bg-purple/5" : ""}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-ink-muted">{label}</div>
      <div className={`mt-2 font-display font-semibold text-ink ${big ? "text-3xl md:text-4xl text-purple" : "text-xl"}`}>{value}</div>
      {note && <div className="mt-2 text-xs text-ink-muted leading-relaxed">{note}</div>}
    </div>
  );
}

export default function HourlyRateCalculator() {
  const [f, setF] = useState(DEFAULTS);
  const printRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const reset = () => setF(DEFAULTS);

  const r = useMemo(() => {
    const weeklyIncome = num(f.weeklyIncome);
    const weeklyCosts = num(f.weeklyCosts);
    const paidHours = num(f.paidHours);
    const unpaidHours = num(f.unpaidHours);
    const weeklyMiles = num(f.weeklyMiles);
    const mileageRate = num(f.mileageRate);
    const roundTo = Math.max(num(f.roundTo), 0.01);
    if (paidHours <= 0) return { valid: false };
    const amountNeeded = weeklyIncome + weeklyCosts;
    const unrounded = amountNeeded / paidHours;
    const suggestedRate = Math.ceil(unrounded / roundTo) * roundTo;
    const careRevenue = suggestedRate * paidHours;
    const mileageTotal = weeklyMiles * mileageRate;
    const weeklyQuote = careRevenue + mileageTotal;
    const totalWork = paidHours + unpaidHours;
    const effectiveRate = totalWork > 0 ? (careRevenue - weeklyCosts) / totalWork : 0;
    return { valid: true, suggestedRate, careRevenue, mileageTotal, weeklyQuote, totalWork, effectiveRate };
  }, [f]);

  const summary = r.valid
    ? [
        "JCC Hourly Care Rate Starter",
        `Suggested hourly rate: ${gbp(r.suggestedRate)}`,
        `Income from care fees: ${gbp(r.careRevenue)} / week`,
        `Mileage to invoice separately: ${gbp(r.mileageTotal)} / week`,
        `Example weekly client quote: ${gbp(r.weeklyQuote)}`,
        `Effective return across ${r.totalWork.toFixed(1).replace(/\.0$/, "")} total work hours: ${gbp(r.effectiveRate)} / hour`,
        "",
        "Planning guide only — not financial, legal or tax advice.",
      ].join("\n")
    : "";

  const onCopy = async () => {
    try { await navigator.clipboard.writeText(summary); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* clipboard not available */ }
  };
  const onPrint = () => window.print();

  return (
    <div className="min-h-screen bg-surface-carer" data-testid="page-hourly-rate-calculator">
      <div className="container-jcc py-8 md:py-14 max-w-5xl">
        <Link to="/workspace/pro-carer-circle-hub" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink" data-testid="hourly-back">
          <ArrowLeft className="w-4 h-4" /> Back to Pro Carer Office hub
        </Link>

        <header className="mt-6">
          <div className="overline text-purple">Pricing & Income Tools</div>
          <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl">Hourly Care Rate Starter</h1>
          <p className="mt-3 text-ink-muted max-w-2xl leading-relaxed">
            Plan a sustainable hourly rate that covers your target income, business costs, and the unpaid time behind every placement. Figures stay on your device.
          </p>
        </header>

        <div ref={printRef} className="mt-8 grid lg:grid-cols-5 gap-6">
          <form className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-6 md:p-8 space-y-5" onSubmit={(e)=>e.preventDefault()} data-testid="hourly-form">
            <Field id="weeklyIncome" label="Weekly income target, before personal tax" hint="What you need to take home each week after business costs, before your own tax and NI." value={f.weeklyIncome} onChange={set("weeklyIncome")} prefix="£" />
            <Field id="weeklyCosts" label="Average weekly business costs" hint="Insurance, phone, admin, mileage set-asides, CPD — anything you spend to keep working." value={f.weeklyCosts} onChange={set("weeklyCosts")} prefix="£" />
            <Field id="paidHours" label="Paid care hours per week" hint="Hours that will actually appear on invoices." value={f.paidHours} onChange={set("paidHours")} suffix="hours" />
            <Field id="unpaidHours" label="Unpaid travel and admin hours" hint="Preparation, calls, invoicing, records, family updates — the hours that don't appear on invoices but still cost your week." value={f.unpaidHours} onChange={set("unpaidHours")} suffix="hours" />
            <Field id="weeklyMiles" label="Business miles per week" hint="If you invoice mileage separately, include it here." value={f.weeklyMiles} onChange={set("weeklyMiles")} suffix="miles" />
            <Field id="mileageRate" label="Mileage charge per mile" hint="HMRC 2025/26 self-employed guidance uses 45p for the first 10,000 miles." step="0.05" value={f.mileageRate} onChange={set("mileageRate")} prefix="£" />
            <Field id="roundTo" label="Round your hourly rate up to" hint="Round up to the nearest pound, 50p or 25p." step="0.25" value={f.roundTo} onChange={set("roundTo")} prefix="£" />
          </form>

          <aside className="lg:col-span-2 space-y-4">
            {!r.valid ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-ink-muted" data-testid="hourly-empty">
                Enter at least 0.5 paid care hours to calculate a starting rate.
              </div>
            ) : (
              <>
                <Stat testid="hourly-suggested" big label="Suggested hourly rate" value={`${gbp(r.suggestedRate)} / hour`} note="Rounded up from your income and cost targets over your paid hours." />
                <div className="grid sm:grid-cols-2 gap-3">
                  <Stat testid="hourly-carerev" label="Care fees" value={`${gbp(r.careRevenue)} / week`} />
                  <Stat testid="hourly-mileage" label="Mileage (separate)" value={`${gbp(r.mileageTotal)} / week`} />
                </div>
                <Stat testid="hourly-weekly-quote" label="Example weekly quote to client" value={gbp(r.weeklyQuote)} note="Care fees plus mileage — a typical figure a client would see for a week at this rate." />
                <Stat testid="hourly-effective" label={`Effective return across ${r.totalWork.toFixed(1).replace(/\.0$/, "")} total work hours`} value={`${gbp(r.effectiveRate)} / hour`} note="What you actually earn per hour of work once unpaid admin and travel are counted — the true picture." />
              </>
            )}

            <div className="rounded-2xl bg-white border border-slate-200 p-4 flex flex-wrap gap-2">
              <button type="button" onClick={onPrint} className="btn-outline text-sm" data-testid="hourly-print">
                <Printer className="w-4 h-4" strokeWidth={1.75} /> Print / save
              </button>
              <button type="button" onClick={onCopy} className="btn-outline text-sm" data-testid="hourly-copy">
                <ClipboardCopy className="w-4 h-4" strokeWidth={1.75} /> {copied ? "Copied" : "Copy summary"}
              </button>
              <button type="button" onClick={reset} className="btn-outline text-sm" data-testid="hourly-reset">
                <RotateCcw className="w-4 h-4" strokeWidth={1.75} /> Reset
              </button>
            </div>
          </aside>
        </div>

        <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50/40 p-5 flex gap-3" data-testid="hourly-disclaimer">
          <Info className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" strokeWidth={1.75} />
          <p className="text-sm text-ink-muted leading-relaxed">
            Your suggested rate is a planning guide only. It is not financial, legal or tax advice. Your final rate should reflect your experience, responsibilities, location, insurance, expenses, working pattern and the individual care arrangement.
          </p>
        </div>
      </div>
    </div>
  );
}
